import { debounce } from 'lodash';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  CustomThemeProvider,
  DateRangePicker,
  InputSearch,
  Selector,
  SelectorOptionPropsInterface,
  TaptapList,
  type ThemeType,
  TooltipTag,
  useQueryFetch
} from 'sonata-react-components';

import { getDateSeed } from '@/helpers/common/seed.helper';
import { countriesISO2Options } from '@/helpers/countriesOptions/countriesOptions.helper';
import { getUserId } from '@/helpers/user/user.helpers';
import { getUserVenueCountries, getVenues, getVenuesByIds, updateVenue } from '@/services/venue.service';
import AttributionAndGroup from '../../../Lists/common/AttributionAndGroup/AttributionAndGroup';
import Title from '../../../Lists/common/Title/Title';
import { DeleteVenues } from './DeleteVenues/DeleteVenues';
import { GroupVenues } from './GroupVenues/GroupVenues';
import { getVenueGroupsOptions, getVenuesWithLinkedEntities, type TaptapListItem, venueToRow } from './helpers';
import { VenueMap } from './VenueMap/VenueMap';

import type { Moment } from 'moment-timezone';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import './myVenues.styles.scss';

type Props = {
  onSelectionChange?: (venues: Venue[]) => void;
  user: UserInterface;
  theme?: ThemeType;
};

type Filters = {
  date: {
    beginDate?: Moment;
    endDate?: Moment;
  };
  groups: Venue['groups'];
  country: Venue['country'] | null;
  source: Venue['state'] | null;
  search: string | null;
  sort: { columnId: string; direction: 'asc' | 'desc' } | null;
};

type State = {
  filters: Filters;
  seed: string;
  selectedVenueIds: Venue['_id'][];
  showDeletePrompt: boolean;
  showGroupSelection: boolean;
};

export function MyVenues({ onSelectionChange, user, theme = 'light' }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [expandedVenueId, setExpandedVenueId] = useState<string | null>(null);
  const [{ filters, showDeletePrompt, showGroupSelection, selectedVenueIds, seed }, setState] = useState<State>({
    filters: { country: null, date: {}, groups: [], search: null, sort: null, source: null },
    seed: getDateSeed(),
    selectedVenueIds: [],
    showDeletePrompt: false,
    showGroupSelection: false
  });

  const BASE_ID = 'myVenues';
  const DATE_FORMAT = 'DD-MM-YYYY';
  const userId = getUserId(user);

  const tokens = {
    countries: {},
    ids: {},
    list: {},
    update: {}
  };

  const { data: selectedVenues = [], isLoading: isFetchingSelectedVenues } = useQueryFetch(
    ['user-venues-by-ids', userId, selectedVenueIds],
    async () => getVenuesByIds(selectedVenueIds, userId, tokens.ids),
    {
      enabled: selectedVenueIds.length > 0
    }
  );

  const { data: userVenueCountries } = useQueryFetch(['user-venue-countries', userId], async () =>
    getUserVenueCountries(userId, tokens.countries)
  );

  const fetchVenues = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const payload = {
        country: filters.country,
        date: filters.date,
        groups: filters.groups,
        itemsPerPage: pageSize,
        order: filters.sort?.direction,
        page,
        search: filters.search,
        sortBy: filters.sort?.columnId,
        status: filters.source
      };
      const venues = (await getVenues(payload, userId as string, tokens.list)) as Record<string, any>;
      const venuesWithLinkedEntities = await getVenuesWithLinkedEntities(venues.items);
      return {
        data: venuesWithLinkedEntities.map(venue => venueToRow(venue, user.venueGroups, formatMessage)),
        page: venues.currentPage ?? page,
        pageSize: venues.itemsPerPage ?? pageSize,
        total: venues.totalItems ?? venues.items.length,
        totalPages: Math.max(
          1,
          Math.ceil((venues.totalItems ?? venues.items.length) / (venues.itemsPerPage ?? pageSize))
        )
      };
    },
    [filters]
  );

  const handleContryChange = (countryOption: SelectorOptionPropsInterface): void => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        country: countryOption.id as string
      },
      seed: getDateSeed()
    }));
  };

  const handleSortChange = (columnId: string, direction: 'asc' | 'desc'): void => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        sort: { columnId, direction }
      },
      seed: getDateSeed()
    }));
  };

  const handleSourceChange = (source: SelectorOptionPropsInterface): void => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        source: source.id as string
      },
      seed: getDateSeed()
    }));
  };

  const handleVenueGroupChange = (venueGroups: SelectorOptionPropsInterface[]): void => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        groups: venueGroups.map(vg => vg.id as string)
      },
      seed: getDateSeed()
    }));
  };

  const handleDatesChange = (dates: { endDate: Moment | null; startDate: Moment | null }) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        date: {
          beginDate: dates.startDate ?? undefined,
          endDate: dates.endDate ?? undefined
        }
      },
      ...(((dates.endDate && dates.startDate) || (!dates.endDate && !dates.startDate)) && { seed: getDateSeed() })
    }));
  };

  const handleSearchChange = debounce((search: string) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        search
      },
      seed: getDateSeed()
    }));
  }, 500);

  const handleDeleteClick = (showPrompt: boolean, removed?: boolean) => {
    setState(prev => ({
      ...prev,
      showDeletePrompt: showPrompt,
      ...(removed && {
        seed: getDateSeed(),
        selectedVenueIds: []
      })
    }));
  };

  const handleGroupClick = (showPrompt: boolean, grouped?: boolean) => {
    setState(prev => ({
      ...prev,
      showGroupSelection: showPrompt,
      ...(grouped && {
        seed: getDateSeed(),
        selectedVenueIds: []
      })
    }));
  };

  const handleSelectionChange = (selectedItems: TaptapListItem[]) => {
    setState(prev => ({
      ...prev,
      selectedVenueIds: selectedItems.map(v => v.id)
    }));
    onSelectionChange?.(selectedItems.map(item => item.venue));
  };

  const handleTitleChange = async (newName: string, item: TaptapListItem) => {
    if (!newName || newName === item.title) {
      return;
    }
    await updateVenue({ ...item.venue, name: newName }, userId, tokens.update);
    setState(prev => ({ ...prev, seed: getDateSeed() }));
  };

  const selectorCountryOptions: SelectorOptionPropsInterface[] = countriesISO2Options.filter(c =>
    userVenueCountries?.includes(c.id as string)
  );
  const selectorSourceOptions: SelectorOptionPropsInterface[] = [
    {
      id: 'requested',
      label: formatMessage({
        defaultMessage: 'Beacon Requested',
        id: 'menu.myVenues.filter.source.requested'
      })
    },
    {
      id: 'active',
      label: formatMessage({
        defaultMessage: 'Beacon active',
        id: 'menu.myVenues.filter.source.active'
      })
    }
  ];

  const isDeleteButtonDisabled =
    selectedVenues.length === 0 ||
    selectedVenues.filter(v => v.campaigns?.length || v.insertionOrders?.length).length > 0;
  const isGroupButtonDisabled = selectedVenues.length === 0;

  return (
    <CustomThemeProvider themeContrast={theme}>
      <div className="myVenues">
        <div className="myVenues__filters">
          <Selector
            className="myVenues__filtersCountry"
            id={`${BASE_ID}_venueCountrySelector`}
            isClearable
            label={formatMessage({
              defaultMessage: 'Country',
              id: 'menu.myVenues.filter.country'
            })}
            onChange={handleContryChange}
            options={selectorCountryOptions}
            showSearch
            tiny
            type="simple"
          />
          <Selector
            className="myVenues__filtersSource"
            id={`${BASE_ID}_venueSourceSelector`}
            isClearable
            label={formatMessage({
              defaultMessage: 'Data Source',
              id: 'menu.myVenues.filter.source'
            })}
            onChange={handleSourceChange}
            options={selectorSourceOptions}
            showSearch
            tiny
            type="simple"
          />
          <Selector
            className="myVenues__filtersGroup"
            id={`${BASE_ID}_venueGroupSelector`}
            label={formatMessage({
              defaultMessage: 'Venue Group',
              id: 'menu.myVenues.filter.group'
            })}
            onChange={handleVenueGroupChange}
            options={getVenueGroupsOptions(user)}
            showSearch
            tiny
            type="multiple"
          />
          <DateRangePicker
            displayFormat={DATE_FORMAT}
            endDate={filters.date.endDate ?? null}
            endDateId={`${BASE_ID}_date_range_end_date`}
            focusedInput={null}
            inputIconPosition="after"
            isOutsideRange={() => false}
            label={formatMessage({
              defaultMessage: 'Date Filter',
              id: 'menu.myVenues.filter.date.label'
            })}
            minimumNights={0}
            onDatesChange={handleDatesChange}
            onFocusChange={() => null}
            showClearDates
            startDate={filters.date.beginDate ?? null}
            startDateId={`${BASE_ID}_date_range_start_date`}
            tiny
          />
          <InputSearch
            className="myVenues__filtersText"
            id={`${BASE_ID}_searchByText`}
            lens
            name="myVenuesSearch"
            onChange={handleSearchChange}
            placeholder={formatMessage({
              defaultMessage: 'Search by name, category...',
              id: 'menu.myVenues.filter.textPlaceholder'
            })}
            tiny
          />
        </div>
        <div className="myVenues__listContainer">
          <TaptapList
            className="myVenues__list"
            fetchData={fetchVenues}
            id="myVenues-list"
            key={seed.toString()}
            onSelectionChange={handleSelectionChange}
            onSortChange={handleSortChange}
            selectionType="checkboxTop"
          >
            <TaptapList.Toolbar>
              <TaptapList.Toolbar.SelectionSummary />
              <div className="myVenues__toolbarActions">
                <ButtonUI
                  className="myVenues__resumeDelete"
                  disabled={isDeleteButtonDisabled}
                  icon="fa-light fa-trash"
                  id={`${BASE_ID}_deleteBtn`}
                  loading={isFetchingSelectedVenues}
                  onClick={() => handleDeleteClick(true)}
                  size="ultraSmall"
                  text={formatMessage({
                    defaultMessage: 'Delete',
                    id: 'common.delete'
                  })}
                  type="tertiary"
                />
                <ButtonUI
                  className="myVenues__resumeGroup"
                  disabled={isGroupButtonDisabled}
                  icon="fa-solid fa-object-intersect"
                  id={`${BASE_ID}_groupBtn`}
                  loading={isFetchingSelectedVenues}
                  onClick={() => handleGroupClick(true)}
                  size="ultraSmall"
                  text={formatMessage({
                    defaultMessage: 'Group selection',
                    id: 'menu.myVenues.resume.group'
                  })}
                  type="secondary"
                />
              </div>
            </TaptapList.Toolbar>
            <TaptapList.ColumnsBar>
              <TaptapList.ColumnsBar.SelectAll />
              <TaptapList.ColumnsBar.Column
                className="venueName"
                description={formatMessage({
                  defaultMessage: 'Name & Address',
                  id: 'menu.myVenues.list.column.name'
                })}
                id="name"
                sortable
              />
              <TaptapList.ColumnsBar.Column
                className="venueGroups"
                description={formatMessage({
                  defaultMessage: 'Groups',
                  id: 'menu.myVenues.list.column.group'
                })}
                id="groups"
              />
              <TaptapList.ColumnsBar.Column
                className="venueLinkedLIs"
                description={formatMessage({
                  defaultMessage: 'Linked LIs',
                  id: 'menu.myVenues.list.column.linkedLis'
                })}
                id="linkedLis"
              />
              <TaptapList.ColumnsBar.Column
                className="venueLinkedIOs"
                description={formatMessage({
                  defaultMessage: 'Linked IOs',
                  id: 'menu.myVenues.list.column.linkedIos'
                })}
                id="linkedIos"
              />
            </TaptapList.ColumnsBar>
            <TaptapList.RowsList<TaptapListItem>
              emptyStateDescription={formatMessage({
                defaultMessage: 'Try adjusting your search or filters to find venues.',
                id: 'menu.myVenues.list.emptyState.description'
              })}
              emptyStateIcon="fal fa-empty-set"
              emptyStateTitle={formatMessage({
                defaultMessage: 'No results match your filters',
                id: 'menu.myVenues.list.emptyState.title'
              })}
            >
              {items =>
                items.map(item => (
                  <TaptapList.RowsList.Row
                    id={item.id}
                    key={item.id}
                    onCustomRowClick={() => setExpandedVenueId(prev => (prev === item.id ? null : item.id))}
                  >
                    <span className="venueName">
                      <Title
                        iconPosition="right"
                        onTextChange={(newName: string) => handleTitleChange(newName, item)}
                        rowId={item.id}
                        text={item.title}
                      />
                    </span>
                    <span className="venueCoordinates">
                      <i className="fa-solid fa-map-pin" />
                      {item.coordinates}
                    </span>
                    <span className="venueGroups">
                      <AttributionAndGroup groups={item.groups} venueId={item.id} />
                    </span>
                    <span className="venueLinkedIOs">
                      <TooltipTag
                        id={`venueGroups-IO-${item.id}`}
                        status="category"
                        tagConfig={{
                          tagChildren: item.linkedIOs.length
                        }}
                      >
                        {item.linkedIOs.length ? (
                          <ul>
                            {item.linkedIOs.map(io => (
                              <li key={io._id}>{io.name}</li>
                            ))}
                          </ul>
                        ) : undefined}
                      </TooltipTag>
                    </span>
                    <span className="venueLinkedLIs">
                      <TooltipTag
                        id={`venueGroups-LI-${item.id}`}
                        status="category"
                        tagConfig={{
                          tagChildren: item.linkedLIs.length
                        }}
                      >
                        {item.linkedLIs.length ? (
                          <ul>
                            {item.linkedLIs.map(li => (
                              <li key={li._id}>{li.name}</li>
                            ))}
                          </ul>
                        ) : undefined}
                      </TooltipTag>
                    </span>
                    <TaptapList.RowsList.Row.Actions actions={item.actions} />
                    {expandedVenueId === item.id && item.venue.latlng && (
                      <span className="venueExpand" data-row-action>
                        <VenueMap venue={item.venue} />
                      </span>
                    )}
                  </TaptapList.RowsList.Row>
                ))
              }
            </TaptapList.RowsList>
          </TaptapList>
        </div>
        {showDeletePrompt && (
          <DeleteVenues
            onCancel={() => handleDeleteClick(false)}
            onConfirm={() => handleDeleteClick(false, true)}
            user={user}
            venueIds={selectedVenueIds}
          />
        )}
        {showGroupSelection && (
          <GroupVenues
            onCancel={() => handleGroupClick(false)}
            onConfirm={() => handleGroupClick(false, true)}
            user={user}
            venues={selectedVenues as Venue[]}
          />
        )}
      </div>
    </CustomThemeProvider>
  );
}
